import React, { useEffect, useMemo, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";

const STATUS_TABS = [
  { key: "new", label: "New" },
  { key: "ready", label: "Ready" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "later", label: "Later" },
  { key: "sent", label: "Sent" },
  { key: "passed", label: "Passed" },
];

async function apiFetch(path, { token, method = "GET", body } = {}) {
  const res = await fetch(path, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });

  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const msg = json?.message || json?.error || `HTTP ${res.status}`;
    const err = new Error(msg);
    err.status = res.status;
    err.payload = json;
    throw err;
  }
  return json;
}

export default function LeadInboxLite() {
  const { isAuthenticated, loginWithRedirect, getAccessTokenSilently } = useAuth0();

  const [status, setStatus] = useState("new");
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [draftModal, setDraftModal] = useState({ open: false, draft: null, lead: null });

  const statusTabs = useMemo(() => STATUS_TABS, []);

  async function getToken() {
    // Uses Auth0 audience/scope config already present in app
    return await getAccessTokenSilently();
  }

  async function load() {
    if (!isAuthenticated) return;
    setLoading(true);
    setError(null);
    try {
      const token = await getToken();
      const data = await apiFetch(`/api/lead-inbox?status=${encodeURIComponent(status)}`, { token });
      setItems(data.items || []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, status]);

  async function onFeedback(leadId, verdict) {
    try {
      const token = await getToken();
      await apiFetch(`/api/lead-inbox/${leadId}/feedback`, {
        token,
        method: "POST",
        body: { verdict },
      });
      await load();
    } catch (e) {
      alert(e.message);
    }
  }

  async function onDraft(lead) {
    try {
      const token = await getToken();
      const data = await apiFetch(`/api/lead-inbox/${lead.id}/draft`, {
        token,
        method: "POST",
      });
      setDraftModal({ open: true, draft: data.draft, lead });
      await load();
    } catch (e) {
      alert(e.message);
    }
  }

  async function onSeed() {
    try {
      const token = await getToken();
      await apiFetch(`/api/lead-inbox/seed`, { token, method: "POST", body: { count: 10 } });
      await load();
    } catch (e) {
      alert(e.message);
    }
  }

  if (!isAuthenticated) {
    return (
      <div style={{ padding: 24, maxWidth: 900, margin: "0 auto" }}>
        <h1 style={{ fontSize: 28, marginBottom: 8 }}>Lead Inbox</h1>
        <p style={{ marginBottom: 16 }}>Log in to view your leads.</p>
        <button
          onClick={() => loginWithRedirect()}
          style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #ddd" }}
        >
          Log in
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: 24, maxWidth: 1100, margin: "0 auto" }}>
      <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
        <div>
          <h1 style={{ fontSize: 28, marginBottom: 6 }}>Lead Inbox (Lite)</h1>
          <div style={{ color: "#666" }}>Tenant-scoped inbox + feedback + drafts (draft-only)</div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button
            onClick={onSeed}
            style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #ddd", background: "#fff" }}
            title="Seeds 10 placeholder leads for your tenant"
          >
            Seed 10
          </button>
          <button
            onClick={load}
            style={{ padding: "10px 14px", borderRadius: 10, border: "1px solid #ddd", background: "#fff" }}
          >
            Refresh
          </button>
        </div>
      </div>

      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16 }}>
        {statusTabs.map((t) => (
          <button
            key={t.key}
            onClick={() => setStatus(t.key)}
            style={{
              padding: "8px 12px",
              borderRadius: 999,
              border: "1px solid #ddd",
              background: status === t.key ? "#5a20ff" : "#fff",
              color: status === t.key ? "#fff" : "#111",
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {error && (
        <div style={{ marginTop: 16, padding: 12, border: "1px solid #f2c", borderRadius: 10, color: "#900" }}>
          {error}
        </div>
      )}

      <div style={{ marginTop: 18 }}>
        {loading ? (
          <div>Loading…</div>
        ) : items.length === 0 ? (
          <div style={{ color: "#666" }}>No leads in “{status}”.</div>
        ) : (
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))", gap: 12 }}>
            {items.map((lead) => (
              <div
                key={lead.id}
                style={{
                  border: "1px solid #eee",
                  borderRadius: 14,
                  padding: 14,
                  background: "#fff",
                }}
              >
                <div style={{ fontWeight: 700, fontSize: 16 }}>
                  <a href={`/lead-inbox/item/${lead.id}`} style={{ color: "#111", textDecoration: "none" }}>
                    {lead.person_name || "(no name)"}
                  </a>
                </div>
                <div style={{ color: "#444", marginTop: 2 }}>
                  {(lead.person_title || "") + (lead.company_name ? ` @ ${lead.company_name}` : "")}
                </div>
                {lead.location && <div style={{ color: "#666", marginTop: 6 }}>{lead.location}</div>}

                {lead.why_now && (
                  <div style={{ marginTop: 10, padding: 10, borderRadius: 10, background: "#f7f7ff" }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: "#5a20ff" }}>WHY NOW</div>
                    <div style={{ marginTop: 6, whiteSpace: "pre-wrap" }}>{lead.why_now}</div>
                  </div>
                )}

                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 12 }}>
                  <button
                    onClick={() => onFeedback(lead.id, "approved")}
                    style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd", background: "#fff" }}
                  >
                    Approve
                  </button>
                  <button
                    onClick={() => onFeedback(lead.id, "rejected")}
                    style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd", background: "#fff" }}
                  >
                    Reject
                  </button>
                  <button
                    onClick={() => onFeedback(lead.id, "later")}
                    style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd", background: "#fff" }}
                  >
                    Later
                  </button>
                  <button
                    onClick={() => onDraft(lead)}
                    style={{ padding: "8px 10px", borderRadius: 10, border: "1px solid #ddd", background: "#fff" }}
                  >
                    Generate Draft
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {draftModal.open && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={() => setDraftModal({ open: false, draft: null, lead: null })}
        >
          <div
            style={{ background: "#fff", borderRadius: 14, padding: 16, maxWidth: 780, width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "center" }}>
              <div style={{ fontWeight: 800 }}>Draft (saved)</div>
              <button
                onClick={() => setDraftModal({ open: false, draft: null, lead: null })}
                style={{ padding: "6px 10px", borderRadius: 10, border: "1px solid #ddd", background: "#fff" }}
              >
                Close
              </button>
            </div>

            <div style={{ marginTop: 10, color: "#555" }}>
              Lead: {draftModal.lead?.person_name} {draftModal.lead?.company_name ? `@ ${draftModal.lead?.company_name}` : ""}
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#666" }}>SUBJECT</div>
              <div style={{ marginTop: 6, padding: 10, border: "1px solid #eee", borderRadius: 10 }}>
                {draftModal.draft?.subject || "(none)"}
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: "#666" }}>BODY</div>
              <pre
                style={{
                  marginTop: 6,
                  padding: 10,
                  border: "1px solid #eee",
                  borderRadius: 10,
                  whiteSpace: "pre-wrap",
                  fontFamily: "ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace",
                }}
              >
                {draftModal.draft?.body || ""}
              </pre>
              <div style={{ marginTop: 8, color: "#888", fontSize: 12 }}>
                Draft-only: nothing is sent from here.
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
